/* eslint-disable no-custom-hooks-in-presentation-components/no-custom-hooks-in-presentation-components */
/* eslint-disable no-state-in-presentation-components/no-state-in-presentation-components */
import { useId } from '@mantine/hooks';
import type { Filter } from '@repo/api-codegen';
import { apiQueryKey, useApiPatchBiMetricDashboards } from '@repo/api-codegen';
import { iconEmojiSelectorValueToString } from '@repo/common/components/IconEmojiSelector';
import { DEFAULT_ITEM_SIZE } from '@repo/common/components/MultiSelector/constants';
import MultiSelector from '@repo/common/components/MultiSelector/MultiSelector';
import { SelectablePropertyItem } from '@repo/common/components/SingleSelector/types';
import { EntityType } from '@repo/common/enums/entityType';
import { IconButton } from '@repo/foundations';
import type { SecodaThemeShades } from '@repo/theme/types';
import { useMemo, useState } from 'react';
import type { ISecodaEntity } from '../../../api';
import { queryClient } from '../../../api';
import { useCatalogInfiniteList } from '../../../api/hooks/resourceCatalog';
import { mapEntityTypeToIcon } from '../../../utils/integrationLogo';
import type { BIMetricStore } from '../store';

interface AddToDashboardMenuProps {
	store: BIMetricStore;
	onDashboardChange?: (dashboardIds: string[]) => void;
	size?: 'sm' | 'md' | 'lg';
}

export function AddToDashboardMenu({
	store,
	onDashboardChange,
	size = 'md',
}: AddToDashboardMenuProps) {
	const uuid = useId();

	const [selected, setSelected] = useState<string[]>(
		store.originalMetric.dashboards.map((d) => d.id)
	);

	// Fetch all available dashboards using catalog infinite list
	const { data: results, isFetching: isLoadingDashboards } =
		useCatalogInfiniteList({
			filters: {
				filter: {
					operator: 'and',
					operands: [
						{
							field: 'entity_type',
							operator: 'exact',
							value: EntityType.bi_dashboard,
							operands: [],
						},
					],
				} as Filter,
				page_size: 100,
				sort: { field: 'title', order: 'asc' },
			},
		});

	// Mutation for updating metric dashboards
	const { mutateAsync: patchBiMetricDashboardsMutation } =
		useApiPatchBiMetricDashboards({});

	// Get the flattened paginated results (already flattened by the hook)
	const dashboards = useMemo(() => results?.pages ?? [], [results]);

	const memoizedDashboardsOptions = useMemo(
		() =>
			dashboards.map(
				(dashboard: ISecodaEntity) =>
					({
						label: dashboard.title || 'Untitled Dashboard',
						value: dashboard.search_metadata?.bi_dashboard_id || '',
						color: 'blue' as SecodaThemeShades,
						itemTypeName: 'dashboard',
						description: dashboard.description || undefined,
						icon:
							dashboard.icon ||
							iconEmojiSelectorValueToString(
								mapEntityTypeToIcon(EntityType.bi_dashboard)!
							),
					}) as SelectablePropertyItem
			),
		[dashboards]
	);

	const handleChange = async (value: (string | boolean)[]) => {
		const newSelected = value as string[];
		setSelected(newSelected);

		// Update the metric's dashboard assignments
		await patchBiMetricDashboardsMutation({
			pathParams: { biMetricId: store.metricId },
			body: { dashboard_ids: newSelected },
		});

		for (const id of newSelected) {
			queryClient.invalidateQueries({
				queryKey: apiQueryKey('intelligence/bi_dashboards/{bi_dashboard_id}', {
					bi_dashboard_id: id,
				}),
			});
		}

		// Notify parent component
		onDashboardChange?.(newSelected);
	};

	return (
		<MultiSelector
			searchPlaceholder="Add to dashboards..."
			target={
				<IconButton
					iconName="layoutGridAdd"
					variant="tertiary"
					tooltip="Add to dashboard"
					size={size}
				/>
			}
			iconType="emoji"
			variant={'default'}
			isViewerUser={false}
			options={memoizedDashboardsOptions}
			onChange={handleChange}
			initialSelected={selected}
			property={'dashboards'}
			permittedId={uuid}
			isMenuItemBadge={false}
			withDescriptionTooltip
			noMaxHeightDropdown
			virtuosoHeight={`min(50vh, ${20 * DEFAULT_ITEM_SIZE}px)`}
		/>
	);
}
